<script setup>
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const emit = defineEmits(['paging-changed'])

const props = defineProps({
    paging: {
        type: Object,
        required: true,
    },
    pageSizes: {
        type: Array,
        required: false,
        default: () => ['10', '15', '25', '50', '100']
    },
    recordName: {
        type: String,
        required: false,
    },
    hideRecordName: {
        type: Boolean,
        required: false,
        default: false,
    },
    countLimit : {
        type : Number,
        required: false,
        default: null,
    },
    pageSizeLabel: {
        type: String,
        required: false,
        default: 'Results per page',
    },
})

const store = useStore()

// Free tier users are limited to 10 results per page
const isFreeTier = computed(() => store.getters.selfSubscriptionTier === 'trial')
const actualPerPage = computed(() => isFreeTier.value ? 10 : props.paging.perPage)

const onPageSizeChange = (value) => {
    const perPage = isFreeTier.value ? 10 : Number(value)
    const totalPages = Math.ceil(props.paging.totalRecords / perPage)
    let currentPage = props.paging.currentPage

    if (currentPage > totalPages) {
        currentPage = totalPages
    }

    return {
        perPage,
        totalPages,
        currentPage,
    }
}

const currentPerPage = computed(() => String(props.paging.perPage))

// Ensure perPage is enforced if the user is on the free tier
watch(
    [isFreeTier, currentPerPage],
    ([isFreeTierUser, perPage]) => {
        if (isFreeTierUser && perPage !== '10') {
            emit('paging-changed', onPageSizeChange('10'))
        }
    },
    { immediate: true }
)
</script>

<template>
    <div class="w-full space-y-2 lg:space-y-0 lg:flex lg:items-center lg:justify-between">
        <div
            v-if="props.paging.totalRecords > 0 && !hideRecordName"
            class="text-sm font-medium"
        >
            <template v-if="countLimit && props.paging.totalRecords >= countLimit">Over </template>
            {{ $numeral(props.paging.totalRecords).format('0,0') }}
            {{ recordName == null ? 'active creators' : recordName }}
        </div>

        <div class="flex w-full items-center justify-between lg:ml-auto lg:w-auto lg:justify-end lg:space-x-8">
            <div class="flex items-center lg:space-x-2">
                <p class="text-sm font-medium lg:block hidden">{{ pageSizeLabel }}</p>
                <p class="text-sm font-medium block mr-2 lg:hidden">Per page</p>
                <Select
                    :model-value="String(actualPerPage)"
                    @update:model-value="(value) => $emit('paging-changed', onPageSizeChange(value))"
                >
                    <SelectTrigger class="h-8 w-[70px]">
                        <SelectValue :placeholder="String(actualPerPage)" />
                    </SelectTrigger>
                    <SelectContent side="top">
                        <SelectItem
                            v-for="pageSize in pageSizes"
                            :key="pageSize"
                            :value="String(pageSize)"
                            :disabled="isFreeTier && pageSize !== '10'"
                            :locked="isFreeTier && pageSize !== '10'"
                        >
                            <span class="flex items-center gap-1">
                                {{ pageSize }}
                            </span>
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div class="flex items-center space-x-2">
                <div class="text-sm font-medium hidden lg:block">
                    Page {{ props.paging.currentPage }} of
                    {{ props.paging.totalPages > 0 ? $numeral(props.paging.totalPages).format('0,0') : 'all pages' }}
                </div>
                <div class="text-sm font-medium lg:hidden">
                    {{ props.paging.currentPage }} of
                    {{ props.paging.totalPages > 0 ? $numeral(props.paging.totalPages).format('0,0') : 'all' }}
                </div>
                <Button
                    variant="outline"
                    class="hidden h-8 w-8 p-0 lg:flex"
                    :disabled="props.paging.currentPage <= 1"
                    @click="$emit('paging-changed', { currentPage: 1 })"
                >
                    <span class="sr-only">Go to first page</span>
                    <ChevronsLeft class="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    class="h-8 w-8 p-0"
                    :disabled="props.paging.currentPage <= 1"
                    @click="$emit('paging-changed', { currentPage: props.paging.currentPage - 1 })"
                >
                    <span class="sr-only">Go to previous page</span>
                    <ChevronLeft class="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    class="h-8 w-8 p-0"
                    :disabled="props.paging.currentPage === props.paging.totalPages"
                    @click="$emit('paging-changed', { currentPage: props.paging.currentPage + 1 })"
                >
                    <span class="sr-only">Go to next page</span>
                    <ChevronRight class="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    class="hidden h-8 w-8 p-0 lg:flex"
                    :disabled="!(props.paging.totalPages > 0) || props.paging.currentPage === props.paging.totalPages"
                    @click="$emit('paging-changed', { currentPage: props.paging.totalPages })"
                >
                    <span class="sr-only">Go to last page</span>
                    <ChevronsRight class="h-4 w-4" />
                </Button>
            </div>
        </div>
    </div>
</template>
